import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Container
} from '@mui/material';
import {
  ErrorOutline,
  Refresh,
  Home
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { APP_CONFIG } from '../../utils/constants';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Atualiza o state para mostrar a UI de erro
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log do erro para monitoramento
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Aqui você poderia enviar o erro para um serviço de monitoramento
    // como Sentry, LogRocket, etc.
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                textAlign: 'center',
                p: 4
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3
                  }}
                >
                  {/* Ícone de erro */}
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: '50%',
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ErrorOutline sx={{ fontSize: 48 }} />
                  </Box>

                  {/* Título e mensagem */}
                  <Box>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="text.primary">
                      Ops! Algo deu errado
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      Ocorreu um erro inesperado na aplicação
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                      Não se preocupe, nossa equipe foi notificada e está trabalhando para resolver o problema. 
                      Tente recarregar a página ou voltar ao início.
                    </Typography>
                  </Box>

                  {/* Detalhes do erro (apenas em desenvolvimento) */}
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        width: '100%', 
                        textAlign: 'left',
                        '& .MuiAlert-message': {
                          width: '100%'
                        }
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Erro (modo desenvolvimento):
                      </Typography>
                      <Typography variant="body2" component="pre" sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        maxHeight: 200,
                        overflow: 'auto'
                      }}>
                        {this.state.error.toString()}
                        {this.state.errorInfo.componentStack}
                      </Typography>
                    </Alert>
                  )}

                  {/* Botões de ação */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={this.handleReload}
                      sx={{ borderRadius: 2 }}
                    >
                      Recarregar Página
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Home />}
                      onClick={this.handleGoHome}
                      sx={{ borderRadius: 2 }}
                    >
                      Voltar ao Início
                    </Button>
                  </Box>

                  {/* Informações de contato */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Se o problema persistir, entre em contato com o suporte.
                    </Typography>
                    <Typography variant="body2" color="primary.main" fontWeight="medium">
                      {APP_CONFIG.FULL_NAME} v{APP_CONFIG.VERSION}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;






